import { ApprovedTransactionsColumns } from "@/core/services/api/interfaces/local/ApprovedTransactionsColumns";
import { PendingTransactionsColumns } from "@/core/services/api/interfaces/local/PendingTransactionsColumns";
import { ImportsColumns } from "@/core/services/api/interfaces/local/ImportsColumns";
import { ReportsColumns } from "@/core/services/api/interfaces/local/ReportsColumns";
import { ArchivesColumns } from "@/core/services/api/interfaces/local/ArchivesColumns";
import { AdminRequireActionTransactionsColumns } from "@/core/services/api/interfaces/local/AdminRequireActionTransactionsColumns";

export enum AttrKey {
  TABLES = "tables",
}

type Columns =
  | ArchivesColumns[]
  | ReportsColumns[]
  | ImportsColumns[]
  | PendingTransactionsColumns[]
  | AdminRequireActionTransactionsColumns[]
  | ApprovedTransactionsColumns[];

export enum TablesAttrKey {
  APPROVED_TRANSACTIONS = "approved_transactions",
  PENDING_TRANSACTIONS = "pending_transactions",
  ADMIN_ACTION_REQUIRE_TRANSACTIONS = "admin_action_require_transactions",
  IMPORTS = "imports",
  REPORTS = "reports",
  ARCHIVES = "archives",
}

export interface ITableSettings<C> {
  columns: C[];
  per_page: number | null;
}
export interface ITablesSettings {
  [TablesAttrKey.APPROVED_TRANSACTIONS]: ITableSettings<ApprovedTransactionsColumns>;
  [TablesAttrKey.PENDING_TRANSACTIONS]: ITableSettings<PendingTransactionsColumns>;
  [TablesAttrKey.ADMIN_ACTION_REQUIRE_TRANSACTIONS]: ITableSettings<AdminRequireActionTransactionsColumns>;
  [TablesAttrKey.IMPORTS]: ITableSettings<ImportsColumns>;
  [TablesAttrKey.REPORTS]: ITableSettings<ReportsColumns>;
  [TablesAttrKey.ARCHIVES]: ITableSettings<ArchivesColumns>;
}
const immutableColumns = {
  [TablesAttrKey.APPROVED_TRANSACTIONS]: [
    ApprovedTransactionsColumns.ID,
    ApprovedTransactionsColumns.ACTIONS,
  ],
  [TablesAttrKey.PENDING_TRANSACTIONS]: [
    PendingTransactionsColumns.ID,
    PendingTransactionsColumns.ACTIONS,
  ],
  [TablesAttrKey.ADMIN_ACTION_REQUIRE_TRANSACTIONS]: [
    PendingTransactionsColumns.ID,
    PendingTransactionsColumns.ACTIONS,
  ],
  [TablesAttrKey.IMPORTS]: [ImportsColumns.ACTIONS],
  [TablesAttrKey.REPORTS]: [ReportsColumns.ACTIONS],
  [TablesAttrKey.ARCHIVES]: [ArchivesColumns.ACTIONS],
};

export interface UserSettingsInterface {
  [AttrKey.TABLES]: ITablesSettings;
}

export class UserSettings implements UserSettingsInterface {
  public [AttrKey.TABLES]: ITablesSettings;

  constructor({ [AttrKey.TABLES]: UserSettingsTables }) {
    this[AttrKey.TABLES] = UserSettingsTables;
  }

  public getTableColumns(table: TablesAttrKey, withImmutable = true) {
    const columns = this[AttrKey.TABLES][table].columns;
    if (withImmutable) {
      return [...new Set([...columns, ...immutableColumns[table]])];
    }

    return columns;
  }

  public getTableColumnsEnum(table: TablesAttrKey) {
    switch (table) {
      case TablesAttrKey.APPROVED_TRANSACTIONS:
        return ApprovedTransactionsColumns;
      case TablesAttrKey.PENDING_TRANSACTIONS:
        return PendingTransactionsColumns;
      case TablesAttrKey.ADMIN_ACTION_REQUIRE_TRANSACTIONS:
        return AdminRequireActionTransactionsColumns;
      case TablesAttrKey.IMPORTS:
        return ImportsColumns;
      case TablesAttrKey.REPORTS:
        return ReportsColumns;
      case TablesAttrKey.ARCHIVES:
        return ArchivesColumns;
      default:
        return {};
    }
  }

  public getTablePerPage(table: TablesAttrKey) {
    return this[AttrKey.TABLES][table].per_page;
  }

  public setTableColumns(table: TablesAttrKey, columns: Columns) {
    this[AttrKey.TABLES][table].columns = columns;
  }

  public setTablePerPage(table: TablesAttrKey, per_page: number) {
    this[AttrKey.TABLES][table].per_page = per_page;
  }
}
